import * as React from "react";
import { useEffect, useState, useContext, useCallback, useMemo } from "react";
import Immutable, { OrderedSet } from "immutable";
import styled, { css } from "styled-components";
import FieldTypesContext from "views/components/contexts/FieldTypesContext";
import { defaultCompare } from "logic/DefaultCompare";
import Spinner from "components/common/Spinner";
import Icon from "components/common/Icon";
import IconButton from "components/common/IconButton";
import Button from "components/bootstrap/Button";
import { JSONClipboardIconButton } from "components/common";
import { Table } from "components/bootstrap";
import { TableHead } from "views/components/datatable";
import { MessageFavoriteFieldsProvider } from "components/common/message/details";
import EditFavoriteFieldsAction from "logview/components/EditFavoriteFieldsAction";
import LogViewMessageDetailsRows from "logview/components/LogViewMessageDetailsRows";
import MessageDetailsContext from "./contexts/MessageDetailsContext";
import LogViewMessageDetailsRow, { LogViewMessageDetailsHeadRow } from "./LogViewMessageDetailsRow";
const Container = styled.div(
  ({ theme }) => css`
    position: absolute;
    top: 0;
    bottom: 0;
    right: 0;
    width: 50%;
    min-width: 450px;
    background: ${theme.colors.global.contentBackground};
    height: 100%;
    padding: 15px 15px 10px 15px;
    border: 1px solid ${theme.colors.gray[80]};
    z-index: 2;
    display: flex;
    flex-direction: column;
  `
);
const Header = styled.div`
  display: flex;
  padding-bottom: 5px;
`;
const TableWrapper = styled.div`
  overflow: auto;
  height: 100%;
`;
const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
`;
const FooterElement = styled.div`
  margin: 3px;
`;
const HeaderIcon = styled.div`
  padding: 2px;
`;
const HeaderTitle = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
`;
const HeaderId = styled.h4(
  ({ theme, $hasId }) => css`
    white-space: nowrap;
    text-overflow: ellipsis;
    font-family: ${$hasId ? theme.fonts.family.monospace : "inherit"};
    color: ${$hasId ? "inherit" : theme.colors.text.secondary};
    overflow: hidden;
    margin-right: 5px;
  `
);
const LogViewMessageDetails = ({
  defaultFields,
  onLoadMessage,
  widgetConfig,
  editing,
  onChangeConfig,
  renderFieldValue = void 0,
  renderFieldName = void 0,
  isFavoriteMessageFieldsEnabled = false
}) => {
  var _a, _b;
  const [message, setMessage] = useState();
  const [currentConfig, setCurrentConfig] = useState(widgetConfig);
  const { activeMessageDetails, selectNextMessage, selectPrevMessage, setActiveMessageDetailsState } = useContext(MessageDetailsContext);
  const { all } = useContext(FieldTypesContext);
  const columns = currentConfig.fields;
  const onClose = useCallback(() => setActiveMessageDetailsState(void 0), [setActiveMessageDetailsState]);
  useEffect(() => {
    if (activeMessageDetails) {
      onLoadMessage(activeMessageDetails).then((response) => {
        setMessage(response);
      });
    }
  }, [activeMessageDetails, onLoadMessage]);
  const closeDetails = useCallback(() => {
    if (!currentConfig.fields.equals(widgetConfig.fields)) {
      onChangeConfig(currentConfig);
    }
    onChangeConfig(currentConfig);
    onClose();
  }, [currentConfig, widgetConfig.fields, onChangeConfig, onClose]);
  const dirty = useCallback(
    () => !currentConfig.fields.equals(OrderedSet(widgetConfig.fields)),
    [currentConfig.fields, widgetConfig.fields]
  );
  const onSelect = useCallback(
    (field) => {
      const newFields = columns.includes(field) ? columns.remove(field) : columns.add(field);
      const newConfig = currentConfig.withFields(newFields);
      setCurrentConfig(newConfig);
    },
    [columns, currentConfig]
  );
  const allSelected = useCallback(() => {
    const fields = Object.keys(message == null ? void 0 : message.formatted_fields);
    return currentConfig.fields.sort().equals(OrderedSet(fields).sort());
  }, [currentConfig.fields, message == null ? void 0 : message.formatted_fields]);
  const onSelectAll = useCallback(() => {
    const fields = Object.keys(message == null ? void 0 : message.formatted_fields);
    if (allSelected()) {
      const newConfig = currentConfig.withFields(defaultFields);
      setCurrentConfig(newConfig);
      return;
    }
    const allNewConfig = currentConfig.withFields(Immutable.OrderedSet(fields));
    setCurrentConfig(allNewConfig);
  }, [allSelected, currentConfig, defaultFields, message == null ? void 0 : message.formatted_fields]);
  const findFieldType = useCallback((field) => {
    var _a2;
    return (_a2 = all.find((f) => f.name === field)) == null ? void 0 : _a2.type;
  }, [all]);
  const sortedRows = useMemo(() => {
    if (!(message == null ? void 0 : message.formatted_fields)) {
      return [];
    }
    return Object.entries(message == null ? void 0 : message.formatted_fields).sort(([key1], [key2]) => defaultCompare(key1, key2));
  }, [message == null ? void 0 : message.formatted_fields]);
  if (!activeMessageDetails) {
    return null;
  }
  if (!message) {
    return /* @__PURE__ */ React.createElement(Spinner, { delay: 300 });
  }
  return /* @__PURE__ */ React.createElement(
    MessageFavoriteFieldsProvider,
    {
      message,
      messageFields: all,
      isFeatureEnabled: isFavoriteMessageFieldsEnabled
    },
    /* @__PURE__ */ React.createElement(Container, { "data-testid": `log-view-message-details-${activeMessageDetails.frontendId}` }, /* @__PURE__ */ React.createElement(Header, null, /* @__PURE__ */ React.createElement(HeaderTitle, null, /* @__PURE__ */ React.createElement(HeaderIcon, null, /* @__PURE__ */ React.createElement(Icon, { name: "mail" })), /* @__PURE__ */ React.createElement(
      HeaderId,
      {
        title: (_a = activeMessageDetails.message._id) != null ? _a : "< message has no id >",
        $hasId: !!activeMessageDetails.message._id
      },
      (_b = activeMessageDetails.message._id) != null ? _b : /* @__PURE__ */ React.createElement("i", null, "< message has no id >")
    )), /* @__PURE__ */ React.createElement("div", null, /* @__PURE__ */ React.createElement(JSONClipboardIconButton, { buttonTitle: "Copy message", content: message.fields }), isFavoriteMessageFieldsEnabled && /* @__PURE__ */ React.createElement(EditFavoriteFieldsAction, null), /* @__PURE__ */ React.createElement(
      IconButton,
      {
        disabled: !selectPrevMessage,
        title: "Previous",
        name: "arrow_upward",
        onClick: selectPrevMessage
      }
    ), /* @__PURE__ */ React.createElement(IconButton, { disabled: !selectNextMessage, title: "Next", name: "arrow_downward", onClick: selectNextMessage }), /* @__PURE__ */ React.createElement(IconButton, { title: "Close", name: "close", onClick: onClose }))), /* @__PURE__ */ React.createElement(TableWrapper, null, /* @__PURE__ */ React.createElement(Table, { condensed: true, hover: true, className: "no-bm" }, /* @__PURE__ */ React.createElement(TableHead, null, /* @__PURE__ */ React.createElement(
      LogViewMessageDetailsHeadRow,
      {
        onSelectAll,
        allSelected,
        hideCheckbox: editing
      }
    )), /* @__PURE__ */ React.createElement("tbody", null, !isFavoriteMessageFieldsEnabled ? sortedRows.map(([field, value]) => /* @__PURE__ */ React.createElement(
      LogViewMessageDetailsRow,
      {
        key: `${field}-${value}`,
        field,
        value,
        type: findFieldType(field),
        onSelect: () => onSelect(field),
        selected: columns.includes(field),
        renderFieldName,
        renderFieldValue,
        hideCheckbox: editing
      }
    )) : /* @__PURE__ */ React.createElement(
      LogViewMessageDetailsRows,
      {
        columns,
        renderFieldName,
        renderFieldValue,
        editing,
        onSelect
      }
    )))), dirty() && /* @__PURE__ */ React.createElement(Footer, null, /* @__PURE__ */ React.createElement(FooterElement, null, /* @__PURE__ */ React.createElement(Button, { title: "Save & Close", bsSize: "small", bsStyle: "primary", onClick: closeDetails }, "Save & Close"))))
  );
};
export default LogViewMessageDetails;
